/**
 * Export Report - Client-side report generation for scan results
 *
 * Generates downloadable reports in multiple formats:
 * - JSON: Structured data export
 * - CSV: Findings table for spreadsheets
 * - HTML: Printable styled report
 * - PDF: Via browser print dialog
 */

import type { SimplifiedResults, RiskDistribution } from "./simplify-results";

// ============================================================================
// TYPES
// ============================================================================

export interface ExportMetadata {
  target: string;
  scannedAt: string;
  exportedAt: string;
  scanId: string;
}

export interface ExportableReport {
  metadata: ExportMetadata;
  riskOverview: {
    riskLevel: string;
    assessment: string;
    findingCount: number;
    urgentCount: number;
  };
  distribution: RiskDistribution;
  urgentCount: number;
  totalFindings: number;
  priorityActions: Array<{
    priority: number;
    title: string;
    description: string;
    category: string;
    severity: string;
  }>;
  categories: Array<{
    category: string;
    label: string;
    status: string;
    issueCount: number;
    statusText: string;
  }>;
}

export type ExportFormat = "json" | "csv" | "html" | "pdf";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Trigger a file download in the browser
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Convert results to exportable format
 */
function toExportableReport(
  results: SimplifiedResults,
  metadata: ExportMetadata
): ExportableReport {
  return {
    metadata,
    riskOverview: {
      riskLevel: results.score.riskLevel,
      assessment: results.score.assessment,
      findingCount: results.score.findingCount,
      urgentCount: results.score.urgentCount,
    },
    distribution: results.distribution,
    urgentCount: results.urgentCount,
    totalFindings: results.totalFindings,
    priorityActions: results.priorityActions.map((action) => ({
      priority: action.priority,
      title: action.title,
      description: action.description,
      category: action.category,
      severity: action.severity,
    })),
    categories: results.categories.map((cat) => ({
      category: cat.category,
      label: cat.label,
      status: cat.status,
      issueCount: cat.issueCount,
      statusText: cat.statusText,
    })),
  };
}

// ============================================================================
// EXPORT GENERATORS
// ============================================================================

/**
 * Generate JSON export
 */
function generateJson(results: SimplifiedResults, metadata: ExportMetadata): string {
  const report = toExportableReport(results, metadata);
  return JSON.stringify({ report }, null, 2);
}

/**
 * Generate CSV export (findings table)
 */
function generateCsv(results: SimplifiedResults, metadata: ExportMetadata): string {
  const lines: string[] = [];

  // Header
  lines.push("Priority,Title,Description,Category,Severity");

  // Data rows
  for (const action of results.priorityActions) {
    const row = [
      action.priority.toString(),
      `"${action.title.replace(/"/g, '""')}"`,
      `"${action.description.replace(/"/g, '""')}"`,
      action.category,
      action.severity,
    ];
    lines.push(row.join(","));
  }

  // Add metadata as comments at the end
  lines.push("");
  lines.push(`# Target: ${metadata.target}`);
  lines.push(`# Scanned: ${metadata.scannedAt}`);
  lines.push(`# Risk Level: ${results.score.riskLevel.toUpperCase()}`);
  lines.push(`# Total Findings: ${results.totalFindings}`);

  return lines.join("\n");
}

/**
 * Generate HTML report
 */
function generateHtml(results: SimplifiedResults, metadata: ExportMetadata): string {
  const riskColors: Record<string, { bg: string; text: string; border: string }> = {
    critical: { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
    high: { bg: "#fff7ed", text: "#ea580c", border: "#fed7aa" },
    medium: { bg: "#fffbeb", text: "#d97706", border: "#fde68a" },
    low: { bg: "#ecfeff", text: "#0891b2", border: "#a5f3fc" },
    info: { bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" },
  };

  const scoreColor = riskColors[results.score.riskLevel] || riskColors.medium;

  const priorityActionsHtml = results.priorityActions
    .map((action) => {
      const color = riskColors[action.severity] || riskColors.medium;
      return `
        <div style="padding: 16px; border: 1px solid ${color.border}; border-radius: 8px; background: ${color.bg}; margin-bottom: 12px;">
          <div style="display: flex; gap: 12px; align-items: flex-start;">
            <span style="background: ${color.text}; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; flex-shrink: 0;">
              ${action.priority}
            </span>
            <div>
              <h4 style="margin: 0 0 4px 0; color: #1f2937; font-size: 14px;">${escapeHtml(action.title)}</h4>
              <p style="margin: 0; color: #6b7280; font-size: 13px;">${escapeHtml(action.description)}</p>
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <span style="background: #f3f4f6; padding: 2px 8px; border-radius: 4px; font-size: 11px; text-transform: uppercase; color: #6b7280;">${action.category}</span>
                <span style="background: ${color.border}; color: ${color.text}; padding: 2px 8px; border-radius: 4px; font-size: 11px; text-transform: uppercase;">${action.severity}</span>
              </div>
            </div>
          </div>
        </div>
      `;
    })
    .join("");

  const categoriesHtml = results.categories
    .map((cat) => {
      const statusColors: Record<string, { bg: string; text: string }> = {
        good: { bg: "#ecfdf5", text: "#059669" },
        warning: { bg: "#fffbeb", text: "#d97706" },
        critical: { bg: "#fef2f2", text: "#dc2626" },
      };
      const color = statusColors[cat.status] || statusColors.good;
      return `
        <div style="padding: 12px 16px; border: 1px solid #e5e7eb; border-radius: 8px; display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #374151; font-weight: 500;">${escapeHtml(cat.label)}</span>
          <span style="background: ${color.bg}; color: ${color.text}; padding: 4px 12px; border-radius: 16px; font-size: 12px;">${escapeHtml(cat.statusText)}</span>
        </div>
      `;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Security Scan Report - ${escapeHtml(metadata.target)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      color: #1f2937;
      line-height: 1.5;
    }
    @media print {
      body { padding: 20px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <header style="text-align: center; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb;">
    <h1 style="margin: 0 0 8px 0; font-size: 24px; color: #111827;">SECURITY SCAN REPORT</h1>
    <h2 style="margin: 0 0 16px 0; font-size: 20px; color: #0891b2; font-weight: 600;">${escapeHtml(metadata.target)}</h2>
    <p style="margin: 0; color: #6b7280; font-size: 14px;">Scanned: ${formatDate(metadata.scannedAt)}</p>
  </header>

  <section style="margin-bottom: 32px; padding: 24px; border: 2px solid ${scoreColor.border}; border-radius: 12px; background: ${scoreColor.bg}; text-align: center;">
    <div style="font-size: 48px; font-weight: bold; color: ${scoreColor.text}; margin-bottom: 8px; text-transform: uppercase;">
      ${results.score.riskLevel} RISK
    </div>
    <div style="font-size: 18px; color: #6b7280; margin-bottom: 8px;">
      ${results.totalFindings} ${results.totalFindings === 1 ? "item" : "items"} found
    </div>
    <p style="margin: 16px 0 0 0; color: #4b5563; font-size: 14px;">
      ${escapeHtml(results.score.assessment)}
    </p>
    <div style="margin-top: 16px; display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; font-size: 12px;">
      <span style="color: #dc2626;">${results.distribution.critical} Critical</span>
      <span style="color: #ea580c;">${results.distribution.high} High</span>
      <span style="color: #d97706;">${results.distribution.medium} Medium</span>
      <span style="color: #0891b2;">${results.distribution.low} Low</span>
    </div>
  </section>

  ${results.priorityActions.length > 0 ? `
  <section style="margin-bottom: 32px;">
    <h3 style="font-size: 16px; color: #111827; margin: 0 0 16px 0; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;">
      PRIORITY ACTIONS
      ${results.urgentCount > 0 ? `<span style="color: #dc2626; font-weight: normal; font-size: 14px;"> (${results.urgentCount} urgent)</span>` : ""}
    </h3>
    ${priorityActionsHtml}
  </section>
  ` : ""}

  ${results.categories.length > 0 ? `
  <section style="margin-bottom: 32px;">
    <h3 style="font-size: 16px; color: #111827; margin: 0 0 16px 0; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;">
      CATEGORY BREAKDOWN
    </h3>
    <div style="display: grid; gap: 8px;">
      ${categoriesHtml}
    </div>
  </section>
  ` : ""}

  <footer style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px;">
    <p>Generated by ASM Toolkit &bull; ${formatDate(metadata.exportedAt)}</p>
  </footer>
</body>
</html>`;
}

/**
 * Open HTML content for printing using safe methods
 * Uses Blob URL for secure content loading
 */
function openForPrint(htmlContent: string): void {
  // Create a blob URL for the HTML content - this is safe as we control the content
  const blob = new Blob([htmlContent], { type: "text/html" });
  const blobUrl = URL.createObjectURL(blob);

  // Open the blob URL in a new window
  const printWindow = window.open(blobUrl, "_blank");

  if (!printWindow) {
    // Fallback: download as HTML if popup blocked
    URL.revokeObjectURL(blobUrl);
    downloadFile(htmlContent, "security-report.html", "text/html");
    return;
  }

  // Clean up blob URL and trigger print after load
  printWindow.onload = () => {
    URL.revokeObjectURL(blobUrl);
    printWindow.print();
  };
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

/**
 * Export scan results in the specified format
 */
export function exportReport(
  results: SimplifiedResults,
  metadata: ExportMetadata,
  format: ExportFormat
): void {
  const safeTarget = metadata.target.replace(/[^a-zA-Z0-9.-]/g, "_");
  const dateStr = new Date(metadata.scannedAt).toISOString().split("T")[0];

  switch (format) {
    case "json": {
      const content = generateJson(results, metadata);
      downloadFile(content, `security-report-${safeTarget}-${dateStr}.json`, "application/json");
      break;
    }

    case "csv": {
      const content = generateCsv(results, metadata);
      downloadFile(content, `security-findings-${safeTarget}-${dateStr}.csv`, "text/csv");
      break;
    }

    case "html": {
      const content = generateHtml(results, metadata);
      downloadFile(content, `security-report-${safeTarget}-${dateStr}.html`, "text/html");
      break;
    }

    case "pdf": {
      // Open HTML in new window and trigger print dialog
      const content = generateHtml(results, metadata);
      openForPrint(content);
      break;
    }
  }
}

/**
 * Get available export formats with metadata
 */
export function getExportFormats(): Array<{
  format: ExportFormat;
  label: string;
  description: string;
  icon: string;
}> {
  return [
    {
      format: "pdf",
      label: "PDF Report",
      description: "Print-ready document",
      icon: "FileText",
    },
    {
      format: "html",
      label: "HTML Report",
      description: "Viewable in browser",
      icon: "Globe",
    },
    {
      format: "json",
      label: "JSON Data",
      description: "Structured data export",
      icon: "Code",
    },
    {
      format: "csv",
      label: "CSV Spreadsheet",
      description: "Findings table for Excel",
      icon: "Table",
    },
  ];
}
